import { Football, GoogleLogo, CaretDown, CaretUp } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { trackEvent } from "@/App";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";

const faqs = [
  {
    category: "GETTING STARTED",
    items: [
      { q: "How do I connect my dynasty league?", a: "Enter your Sleeper username on the home page — no password needed. We pull your league data directly from Sleeper's public API. All your dynasty leagues appear instantly." },
      { q: "What fantasy platforms are supported?", a: "Currently we support Sleeper dynasty leagues. We're exploring ESPN, Yahoo, and MFL integrations for future releases." },
      { q: "Do I need to create an account?", a: "No! You can explore all free-tier features just by entering your Sleeper username. No email, no password. If you want Pro features, you'll sign in with Google." },
      { q: "What scoring formats are supported?", a: "All of them. We auto-detect your league's settings from Sleeper: PPR / Half PPR / Standard, Superflex / 1QB, TE Premium, 6pt passing TD, team count, roster size. Values are adjusted accordingly." },
    ],
  },
  {
    category: "PLAYER VALUES & DATA",
    items: [
      { q: "Where do the player values come from?", a: "Dynasty trade values are sourced from FantasyCalc, which uses real trade data from 150,000+ Sleeper leagues to generate consensus values. These are updated regularly and reflect the latest market." },
      { q: "How accurate are the values?", a: "FantasyCalc values are considered among the most accurate in dynasty — they're derived from actual trades, not opinions. We also apply scoring adjustments for league-specific settings (6pt TD, TE Premium) that FantasyCalc doesn't support natively." },
      { q: "What is the waiver adjustment?", a: "Based on FantasyCalc's methodology: in a 2-for-1 trade, the side receiving 2 players must drop a bench player (~400 value). This adjustment is applied automatically to all trade evaluations for more accurate fairness scoring." },
      { q: "How often are values updated?", a: "Player values are cached for 1 hour, so you always see fresh data. Roster and league data is pulled live from Sleeper on each load." },
      { q: "Are draft picks included in values?", a: "Yes! Individual draft picks (e.g., 2026 Pick 1.01, 1.10) have specific values from FantasyCalc. We use your league's actual draft order from Sleeper to assign correct pick numbers and values." },
    ],
  },
  {
    category: "AI & TRADE TOOLS",
    items: [
      { q: "What AI powers the trade suggestions?", a: "We use OpenAI's GPT-4.1 model for trade analysis, roster analysis, waiver wire suggestions, and draft advice. The AI receives your full roster data, opponent rosters, and league settings to generate specific, actionable recommendations." },
      { q: "How balanced are the AI trade suggestions?", a: "Every AI suggestion goes through post-processing that: 1) Looks up real FantasyCalc values (not trusting AI-reported values), 2) Applies waiver adjustment, 3) Validates asset ownership, and 4) Filters out any trade with >8% value difference. Only balanced, realistic trades are shown." },
      { q: "What's the difference between the Trade Analyzer and Trade Finder?", a: "The Analyzer uses AI to generate creative trade packages for specific players you select. The Finder is algorithmic (no AI cost) — it scans every possible trade combination in your league and surfaces the best mutual-fit deals instantly." },
      { q: "Does the AI know my league's specific settings?", a: "Yes. Every AI prompt includes your league format (Superflex, PPR, TE Premium, 6pt TD), number of starters, flex spots, and each team's positional needs. The AI even knows not to suggest trading a QB in Superflex unless you have 3+ starters." },
    ],
  },
  {
    category: "PRICING & BILLING",
    items: [
      { q: "How much does it cost?", a: "Free tier has no cost — explore your roster values, use the trade calculator, and see contender rankings without paying anything. Pro plans start at $19.99/year for 1 league, $49.99/year for 3 leagues, or $79.99/year for unlimited." },
      { q: "What do I get with the free tier?", a: "Dashboard, roster values, trade calculator, trade evaluator, contender rankings, league history, player compare, and limited versions of AI features (1 AI trade per day, 5 Trade Finder results, preview-only roster analysis)." },
      { q: "Can I try Pro features before paying?", a: "Yes! Free users get a taste of every Pro feature: 1 AI trade suggestion per day, first paragraph of roster analysis, round 1 of mock draft, etc. You see enough to know if it's valuable before upgrading." },
      { q: "Can I cancel my subscription?", a: "Yes, cancel anytime from the Billing page. You keep Pro access until the end of your current annual period. No refunds for partial periods." },
      { q: "What payment methods are accepted?", a: "We use Stripe for secure payment processing. All major credit cards, debit cards, and Apple Pay / Google Pay are accepted." },
    ],
  },
  {
    category: "PRIVACY & SECURITY",
    items: [
      { q: "Is my data safe?", a: "Yes. We use HTTPS encryption, httpOnly session cookies, and never store passwords (authentication is via Google OAuth). Payment processing is handled entirely by Stripe — we never see your card details." },
      { q: "What data do you collect?", a: "Google login: name, email, profile picture. Sleeper: username and user ID. League data is cached temporarily (1 hour). We track feature usage to improve the product. See our full Privacy Policy for details." },
      { q: "Do you share my data?", a: "No. We never sell personal data. We share only what's necessary: roster data with OpenAI for AI analysis (no personal info), email with SendGrid for notifications, and payment data with Stripe." },
    ],
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#1a1a1a]">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-4 text-left">
        <span className={`text-sm font-semibold ${open ? 'text-[#ccff00]' : 'text-white'}`}>{q}</span>
        {open ? <CaretUp size={16} className="text-[#666] shrink-0 ml-4" /> : <CaretDown size={16} className="text-[#666] shrink-0 ml-4" />}
      </button>
      {open && <p className="text-sm text-[#a3a3a3] leading-relaxed pb-4">{a}</p>}
    </div>
  );
}

export default function FaqPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO page="faq" />
      <PublicNav />

      <div className="max-w-3xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-white uppercase mb-4" data-testid="faq-title">
            FREQUENTLY ASKED <span className="text-[#ccff00]">QUESTIONS</span>
          </h1>
          <p className="text-base text-[#a3a3a3]">Everything you need to know about Dynasty Trade Maker.</p>
        </div>

        {faqs.map((cat) => (
          <div key={cat.category} className="mb-10">
            <h2 className="font-heading text-lg text-[#ccff00] uppercase tracking-tight mb-4">{cat.category}</h2>
            <div className="border border-[#262626] bg-[#171717] px-5">
              {cat.items.map((item, idx) => <FaqItem key={idx} {...item} />)}
            </div>
          </div>
        ))}

        <div className="text-center py-8 border-t border-[#262626] mt-8">
          <p className="text-sm text-[#a3a3a3] mb-4">Still have questions? Email us at <a href="mailto:derek@emerald.consulting" className="text-[#ccff00] no-underline">derek@emerald.consulting</a></p>
          <Button onClick={() => window.location.href = "/"} className="h-10 px-6 btn-primary rounded-sm text-xs">GET STARTED FREE</Button>
        </div>
      </div>
    </div>
  );
}
